/**
 * Which enrolled KDS device this display is (ORDR-42, requirement from ORDR-43).
 *
 * paio-ordering has no enrolment API. `seed_pilot_venue` prints
 * `device_id=<uuid>`, someone types it into the display once, and it is kept
 * in this browser's localStorage so it survives the reload a kitchen tablet
 * inevitably gets. It is an identifier, not a secret: the change feed still
 * requires the signed-in session and `ticket:view`.
 *
 * Storage can be absent or throw (private mode, blocked site data), so every
 * access is guarded and a failure reads as "no device stored", which renders
 * the enrolment prompt rather than an empty board.
 */

const KEY = "paio-ordering-ui:kds-device-id";
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export interface DeviceStore {
  read(): string | null;
  /** Returns false when the value is not a UUID or storage refused it. */
  write(deviceId: string): boolean;
  clear(): void;
}

export function isDeviceId(value: string): boolean {
  return UUID.test(value.trim());
}

function storage(): Storage | null {
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch {
    return null;
  }
}

export function createDeviceStore(backing: () => Storage | null = storage): DeviceStore {
  return {
    read() {
      try {
        const value = backing()?.getItem(KEY) ?? null;
        return value !== null && isDeviceId(value) ? value.toLowerCase() : null;
      } catch {
        return null;
      }
    },
    write(deviceId) {
      if (!isDeviceId(deviceId)) {
        return false;
      }
      try {
        const store = backing();
        if (store === null) {
          return false;
        }
        store.setItem(KEY, deviceId.trim().toLowerCase());
        return true;
      } catch {
        return false;
      }
    },
    clear() {
      try {
        backing()?.removeItem(KEY);
      } catch {
        // Nothing to do: a store that cannot be written holds nothing to clear.
      }
    },
  };
}
